import { CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

// Size changes of all lines run one after another (cart keys change after each one).
let queue = Promise.resolve();
const propertiesKey = properties => JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
function sameLine(item, reference, variant = reference.variant_id) {
  return item.variant_id === variant && propertiesKey(item.properties) === propertiesKey(reference.properties)
    && item.selling_plan_allocation?.selling_plan.id === reference.selling_plan_allocation?.selling_plan.id;
}
async function cartRequest(path, body) {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(root + path, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const result = await response.json();
  if (!response.ok || result.errors) throw new Error(result.description || result.message || 'Cart update failed');
  return result;
}
if (!customElements.get('pf-cart-size-editor')) {
  customElements.define('pf-cart-size-editor', class extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      // Bind once per select: rows are moved and re-rendered, and each reconnect used to add one more listener.
      if (!this.select || this.boundSelect === this.select) return;
      this.boundSelect = this.select;
      this.select.addEventListener('change', () => this.changeSize());
    }
    // The line's variant comes from the server-rendered markup (kept current by re-renders), never from state cached in JS.
    get current() { return this.dataset.variantId || this.select?.querySelector('option[selected]')?.value || ''; }
    // The visible size text of the row ("Olive, L"), updated at once with the select.
    showSize(variantId) {
      const label = this.select.querySelector(`option[value="${variantId}"]`)?.textContent.trim();
      const shown = this.shownLabel ?? this.select.querySelector(`option[value="${this.current}"]`)?.textContent.trim();
      const dd = [...(this.closest('tr')?.querySelectorAll('.cart-items__variant dd') || [])].find(el => el.textContent.replace(/[, \s]+$/, '').trim() === shown);
      if (dd && label) dd.textContent = dd.textContent.replace(shown, label);
      this.shownLabel = label;
    }
    // Instant: every choice shows at once and the select stays usable. Only the latest choice is saved;
    // choices made while a save runs are picked up when it ends.
    changeSize() {
      const error = this.querySelector('[role="alert"]');
      error.hidden = true;
      this.wanted = this.select.value;
      this.showSize(this.wanted);
      if (this.running) return;
      this.running = true;
      this.setAttribute('aria-busy', 'true');
      queue = queue.then(async () => {
        try {
          while (this.wanted && this.wanted !== this.current) {
            const next = this.wanted;
            await this.applySize(Number(next), () => this.wanted === next);
          }
        } catch (failure) {
          this.wanted = null;
          this.select.value = this.current;
          this.showSize(this.current);
          error.textContent = this.dataset.error;
          error.hidden = false;
          const sectionId = this.closest('cart-items-component')?.dataset.sectionId;
          if (failure?.message === 'Line changed' && sectionId) sectionRenderer.renderSection(sectionId, { cache: false }).catch(() => {});
        } finally {
          this.running = false;
          this.removeAttribute('aria-busy');
        }
      });
    }
    /**
     * Replaces this line's variant in the cart. The drawer is only re-rendered for the last wanted size,
     * so a quick second choice is never overwritten by the render of the first one.
     * @param {number} nextVariant
     * @param {() => boolean} isLatest
     */
    async applySize(nextVariant, isLatest) {
      const currentVariant = this.current;
      const component = this.closest('cart-items-component');
      const sectionId = component?.dataset.sectionId;
      let added;
      let previous;
      let previousNextQuantity = 0;
      try {
        const cart = await cartRequest('cart.js');
        previous = cart.items.find(item => item.key === this.dataset.key);
        // Only ever change the line this select belongs to, and only from the size it really has in the cart.
        if (!previous || String(previous.variant_id) !== currentVariant) throw new Error('Line changed');
        const gifts = cart.items.filter(line => line.parent_relationship?.parent_key === previous.key);
        previousNextQuantity = cart.items.find(line => sameLine(line, previous, nextVariant))?.quantity || 0;
        const item = { id: nextVariant, quantity: previous.quantity, properties: previous.properties || {} };
        if (previous.selling_plan_allocation) item.selling_plan = previous.selling_plan_allocation.selling_plan.id;
        // Fast path: new size + its free accessories in one request, then remove the old line by key.
        const giftItems = gifts.map(gift => ({ id: gift.variant_id, quantity: gift.quantity, properties: gift.properties || {}, parent_id: nextVariant }));
        const result = await cartRequest('cart/add.js', { items: [item, ...giftItems] });
        added = result.items[0];
        let updated;
        try {
          updated = await cartRequest('cart/change.js', { id: previous.key, quantity: 0,
            ...(sectionId ? { sections: [sectionId], sections_url: location.pathname } : {}) });
        } catch {
          // Keys can change after a discount recalculation: resolve the old line again.
          const latest = await cartRequest('cart.js');
          const previousIndex = latest.items.findIndex(line => sameLine(line, previous));
          if (previousIndex < 0) throw new Error('Previous line missing');
          updated = await cartRequest('cart/change.js', { line: previousIndex + 1, quantity: 0,
            ...(sectionId ? { sections: [sectionId], sections_url: location.pathname } : {}) });
        }
        added = null;
        const replacement = updated.items.find(line => sameLine(line, previous, nextVariant));
        this.dataset.variantId = String(nextVariant);
        if (replacement) this.dataset.key = replacement.key;
        // A newer choice is waiting: keep the customer's screen as it is and save that choice next.
        if (!isLatest()) return;
        // The server render already lists the lines in their stable order (cart-products.liquid + pf-cart-order.js).
        document.dispatchEvent(new CartUpdateEvent(updated, 'pf-cart-size-editor', {
          source: 'pf-cart-size-editor', itemCount: updated.item_count, sections: updated.sections,
        }));
      } catch (failure) {
        if (added && previous) {
          try {
            const latest = await cartRequest('cart.js');
            const index = latest.items.findIndex(line => sameLine(line, previous, nextVariant));
            if (index >= 0) await cartRequest('cart/change.js', { line: index + 1, quantity: previousNextQuantity });
          } catch { location.reload(); return; }
        }
        throw failure;
      }
    }
  });
}
